<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

// [Interart] ref (não computed) pra cada retry regenerar o cache-bust do src
const srcUrl = ref(timeStampAppendedURL(attachment.dataUrl));

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

// [Interart] Fix "áudio travado no 0:00": a mensagem é broadcast via WebSocket no
// commit, mas o ActiveStorage só sobe o arquivo pro storage DEPOIS do commit — o
// preload inicial pode levar 404 e, sem tratamento, o <audio> morre até o F5.
// O player só aparece quando a mídia realmente carregou ('loading' → 'ready');
// erro de carga agenda retry com backoff; esgotou → 'failed' com retry manual.
const mediaState = ref('loading'); // 'loading' | 'ready' | 'failed'
const RETRY_DELAYS = [1000, 2000, 3000, 5000, 8000, 13000];
let retryCount = 0;
let retryTimer = null;
let resumeTime = 0;
let resumeAfterRecover = false;

const { uid } = getCurrentInstance();

const markReady = () => {
  mediaState.value = 'ready';
  retryCount = 0;
  if (resumeTime > 0) {
    try {
      audioPlayer.value.currentTime = resumeTime;
    } catch {
      /* noop */
    }
    resumeTime = 0;
  }
  if (resumeAfterRecover) {
    resumeAfterRecover = false;
    audioPlayer.value?.play().then(
      () => {
        isPlaying.value = true;
      },
      () => {
        isPlaying.value = false;
      }
    );
  }
};

const reloadMedia = () => {
  srcUrl.value = timeStampAppendedURL(attachment.dataUrl);
  nextTick(() => audioPlayer.value?.load());
};

const onMediaError = () => {
  if (mediaState.value === 'ready') {
    // Erro depois de pronto (ex.: URL assinada expirou no meio) — guarda a
    // posição e se estava tocando, pra retomar sozinho após recarregar.
    resumeTime = audioPlayer.value?.currentTime || 0;
    resumeAfterRecover = resumeAfterRecover || isPlaying.value;
    isPlaying.value = false;
  }
  if (retryCount >= RETRY_DELAYS.length) {
    mediaState.value = 'failed';
    return;
  }
  mediaState.value = 'loading';
  const delay = RETRY_DELAYS[retryCount];
  retryCount += 1;
  clearTimeout(retryTimer);
  retryTimer = setTimeout(reloadMedia, delay);
};

const retryManual = () => {
  retryCount = 0;
  mediaState.value = 'loading';
  reloadMedia();
};

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
  markReady();
};

const onCanPlay = () => {
  markReady();
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  duration.value = audioPlayer.value?.duration;
  audioPlayer.value.playbackRate = playbackSpeed.value;
  // Mídia já disponível antes do mount (cache do browser)? Libera o player.
  if (audioPlayer.value.readyState >= 1) markReady();
});

onBeforeUnmount(() => clearTimeout(retryTimer));

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // [Interart] Elemento em erro? Recupera em vez de dar play num player morto.
    if (audioPlayer.value.error) {
      resumeAfterRecover = true;
      onMediaError();
      return;
    }
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play().then(
      () => {
        isPlaying.value = true;
      },
      () => {
        isPlaying.value = false;
        if (audioPlayer.value?.error) {
          resumeAfterRecover = true;
          onMediaError();
        }
      }
    );
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="onLoadedMetadata"
    @canplay="onCanPlay"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @error="onMediaError"
  >
    <source :src="srcUrl" @error="onMediaError" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div
      v-if="mediaState === 'loading'"
      class="flex gap-2 w-full flex-1 items-center justify-start h-8 px-1.5"
    >
      <Icon
        class="size-5 animate-spin text-n-slate-11"
        icon="i-lucide-loader-circle"
      />
      <span class="text-xs text-n-slate-11">Carregando áudio…</span>
    </div>
    <div
      v-else-if="mediaState === 'failed'"
      class="flex gap-2 w-full flex-1 items-center justify-start h-8 px-1.5"
    >
      <Icon class="size-4 text-n-ruby-11" icon="i-lucide-triangle-alert" />
      <span class="text-xs text-n-slate-11">
        Não foi possível carregar o áudio
      </span>
      <button
        class="text-xs font-medium underline text-n-slate-12 p-0 border-0"
        @click="retryManual"
      >
        Tentar de novo
      </button>
    </div>
    <div v-else class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>
